import { GLOSSARY_NAME_INVALID_MESSAGE, validateGlossaryName } from '@/entities/glossary';

export const FORM_LABELS = {
  NAME: '이름',
  DISPLAY_NAME: '표시 이름',
  DESCRIPTION: '설명',
  MUTUALLY_EXCLUSIVE: '상호 배타적',
  DOMAIN_TYPE: '도메인 유형',
  TAGS: '태그',
  OWNERS: '소유자',
  EXPERTS: '전문가',
  DOMAINS: '도메인',
  REVIEWERS: '리뷰어',
  RELATED_TERMS: '관련 용어',
  SYNONYMS: '동의어',
  PARENT: '상위 용어',
} as const;

const metaDataValidationRules = [
  {
    pattern: /^[가-힣a-zA-Z0-9 \-_()]+$/,
    message: '한글, 영문, 숫자, 공백, 기본 문장부호(-, _, (, ))만 사용할 수 있습니다',
  },
];

const glossaryNameValidationRules = [
  {
    validator: (_: unknown, value?: string) => {
      if (value === undefined || value === null || value === '') {
        return Promise.resolve();
      }

      if (validateGlossaryName(value)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(GLOSSARY_NAME_INVALID_MESSAGE));
    },
  },
];

const maxLengthValidationRules = (entityName: string, maxLength: number) => [
  {
    max: maxLength,
    message: `${entityName}은 최대 ${maxLength}자까지 입력 가능합니다`,
  },
];

export const FORM_RULES = {
  REQUIRED_NAME: [{ required: true, message: '이름은 필수입니다' }],
  REQUIRED_DESCRIPTION: [{ required: true, message: '설명은 필수입니다' }],
  REQUIRED_DOMAIN_TYPE: [{ required: true, message: '도메인 유형은 필수입니다' }],
  GLOSSARY_CATEGORY_NAME: [...glossaryNameValidationRules, ...maxLengthValidationRules('용어사전명', 50)],
  GLOSSARY_CATEGORY_DESCRIPTION: maxLengthValidationRules('용어사전 설명', 400),
  GLOSSARY_TERM_NAME: [...glossaryNameValidationRules, ...maxLengthValidationRules('용어명', 50)],
  GLOSSARY_TERM_DESCRIPTION: maxLengthValidationRules('용어 설명', 400),

  DOMAIN_NAME: [...metaDataValidationRules, ...maxLengthValidationRules('도메인명', 50)],
  DOMAIN_DESCRIPTION: maxLengthValidationRules('도메인 설명', 300),

  TAG_CATEGORY_NAME: [...metaDataValidationRules, ...maxLengthValidationRules('태그 카테고리명', 50)],
  TAG_NAME: [...metaDataValidationRules, ...maxLengthValidationRules('태그명', 50)],
};

export const FORM_PLACEHOLDERS = {
  NAME: '이름을 입력해 주세요',
  DISPLAY_NAME: '표시 이름을 입력해 주세요 (선택)',
  DESCRIPTION: '설명을 입력해 주세요',
  OWNERS: '소유자 선택',
  EXPERTS: '전문가 선택',
  REVIEWERS: '리뷰어 선택',
  RELATED_TERMS: '관련 용어를 선택하세요',
  DOMAIN_TYPE: '도메인 유형 선택',
} as const;

export const VALIDATION_RULES = {
  DESCRIPTION_MIN_LENGTH: 30,
  DESCRIPTION_MAX_LENGTH: 300,
  ASSET_DESCRIPTION_MIN_LENGTH: 0,
  ASSET_DESCRIPTION_MAX_LENGTH: 300,
  DOMAIN_DESCRIPTION_MIN_LENGTH: 0,
  DOMAIN_DESCRIPTION_MAX_LENGTH: 300,
  TAG_DESCRIPTION_MIN_LENGTH: 0,
  TAG_DESCRIPTION_MAX_LENGTH: 300,
  GLOSSARY_DESCRIPTION_MIN_LENGTH: 0,
  GLOSSARY_DESCRIPTION_MAX_LENGTH: 400,
  DISPLAY_NAME_MAX_LENGTH: 50,
  PROJECT_NAME_MAX_LENGTH: 50,
  EXPERIMENT_NAME_MAX_LENGTH: 50,
  RUN_NAME_MAX_LENGTH: 50,
  TAG_NAME_MAX_LENGTH: 50,
  SEARCH_MAX_LENGTH: 60,
} as const;

export const VALIDATION_MESSAGES = {
  DESCRIPTION_MIN_LENGTH: '설명은 30자 이상 입력해 주세요',
  DESCRIPTION_MAX_LENGTH: '설명은 최대 300자까지 입력 가능합니다',
  PROJECT_NAME_REQUIRED: '프로젝트명을 입력해 주세요.',
  PROJECT_NAME_MAX_LENGTH: '프로젝트명은 최대 50자입니다.',
  EXPERIMENT_NAME_REQUIRED: '실험명을 입력해 주세요.',
  RUN_NAME_REQUIRED: '이름을 입력해 주세요',
  TAG_NAME_MAX_LENGTH: '태그명은 최대 50자까지 입력 가능합니다',
  NAME_ALREADY_EXISTS: '이미 존재하는 이름입니다. 다른 이름을 입력해 주세요',
  DOMAIN_SEARCH_MAX_LENGTH: '도메인 검색어는 최대 30자까지 입력 가능합니다',
  TAG_SEARCH_MAX_LENGTH: '태그 검색어는 최대 30자까지 입력 가능합니다',
  GLOSSARY_SEARCH_MAX_LENGTH: '용어 검색어는 최대 256자까지 입력 가능합니다',
  DATA_SEARCH_MAX_LENGTH: '검색어는 최대 256자까지 입력 가능합니다',
} as const;

export const UI_TIMING = {
  DROPDOWN_MOUNT_DELAY: 150,
  DEBOUNCE_SEARCH: 300,
  WORKBENCH_PREPARATION_DELAY: 3000,
} as const;
